// noinspection JSVoidFunctionReturnValueUsed

import Vue from "vue"
import VueRouter from "vue-router"
import { applicationState, fieldState, sessionState } from "@/state"
import routes from "@/routes"
import { indexBy } from "@/util/array"
import { defaults } from "@/util/object"
import RouteType from "@/model/route/RouteType"
import SwitchProviderAction from "@/action/actions/SwitchProviderAction"
import DynamicProviderKey from "@/model/provider/DynamicProviderKey"
import { loadLocalesAsync } from "@/plugins/i18n.js"

Vue.use(VueRouter)

class ApplyRouter extends VueRouter {
    routeByPath = indexBy(routes, "path")

    constructor(options) {
        super(options)
        this.beforeEach(this.beforeEachRoute.bind(this))
    }

    beforeEachRoute(to, from, next) {
        const initialProviderKey = sessionState.providerKey
        const initialApplyMode = applicationState.applyMode

        //This logic will allow redirects to pass down meta steps
        if (to.redirectedFrom && to.meta && to.meta.inheritStepFromRedirect && from.params.providerKey) {
            let redirectedRoute = this.routeByPath[to.redirectedFrom]
            const providerKey = from.params.providerKey || to.params.providerKey
            if (providerKey && to.redirectedFrom.includes(providerKey)) {
                redirectedRoute = this.routeByPath[to.redirectedFrom.replace(providerKey, ":providerKey")]
            }
            if (redirectedRoute && redirectedRoute.meta) {
                to.meta.step = redirectedRoute.meta.step
            }
        }

        if (to.params.mode && !to.meta.disableApplyModeSwitch) {
            applicationState.applyMode = to.params.mode
        }

        if (to.params.providerKey && !to.meta.disableProviderSwitch) {
            sessionState.dynamicProviderKey = new DynamicProviderKey(to.params.providerKey)
        }

        if (!to.meta?.public && !sessionState?.session?.webSessionId) {
            this.push({ name: "sessionInvalid" })
            return
        }

        const promises = []
        addPromise(promises, handleProviderOrApplyModeChange(to, from, next, initialProviderKey, initialApplyMode))

        if (promises.length) {
            Promise.all(promises).finally(() => {
                next()
            })
        } else {
            next()
        }
    }
}

const routeByName = indexBy(routes, "name")

routes.forEach(route => {
    extendRoute(route)
})

function addPromise(promises, promise) {
    if (promise) {
        promises.push(promise)
    }
}

function extendRoute(route) {
    if (!route.extends) return
    const parent = routeByName[route.extends]
    if (!parent) return
    route.meta = defaults(route.meta || {}, parent.meta || {})
}

function handleProviderOrApplyModeChange(to, from, next, initialProviderKey, initialApplyMode) {
    if (initialProviderKey === sessionState.providerKey && initialApplyMode === applicationState.applyMode) {
        return
    }

    const routeType = to.meta?.routeType

    if (routeType !== RouteType.FIELD_COLLECTION) {
        return
    }

    const cachedFields = fieldState.cachedFields(sessionState.providerKey, applicationState.applyMode)

    if (cachedFields) {
        fieldState.fields = cachedFields
        return
    }

    return new SwitchProviderAction(sessionState.providerKey, applicationState.applyMode).invoke()
}

const router = new ApplyRouter({
    base: process.env.BASE_URL,
    routes,
    scrollBehavior() {
        return { x: 0, y: 0 }
    },
})

export default router
